import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { api, productImage } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useCart } from "../../contexts/CartContext";
import { useSeoHead, productSchema, breadcrumbSchema } from "../../lib/seo";
import { faqJsonLd } from "../../lib/productFaq.mjs";
import BoxOptionsStrip from "./BoxOptionsStrip";
import BoxGallery from "./BoxGallery";
import BoxConfigurator from "./BoxConfigurator";
import PriceGrid from "./PriceGrid";
import BoxSizeSpecs from "./BoxSizeSpecs";
import { BoxTeasers, BoxDetails } from "./BoxSections";

const DEFAULTS = (p) => ({
  sizeKey: (p.sizes.find((s) => s.is_default) || p.sizes[0]).key,
  printKey: p.color_options.find((o) => o.key === "print_4_4")?.key || p.color_options[0].key,
  qty: 48,
  turnaroundKey: p.turnaround_options[0]?.key || "",
});

export default function BoxProductPage({ product }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addItem } = useCart();
  const [cfg, setCfg] = useState(() => DEFAULTS(product));
  const [price, setPrice] = useState(null);
  const [calcing, setCalcing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const set = (k) => (v) => setCfg((c) => ({ ...c, [k]: v }));

  const compute = useCallback(async () => {
    setCalcing(true);
    try {
      const { data } = await api.post("/products/price-calc", {
        product_id: product.product_id,
        quantity: cfg.qty,
        material_key: product.materials[0].key,
        size_key: cfg.sizeKey,
        color_key: cfg.printKey,
        turnaround_key: cfg.turnaroundKey,
      });
      setPrice(data);
    } catch (e) {
      setPrice(null);
      const detail = e?.response?.data?.detail;
      if (detail) toast.error(String(detail));
    } finally {
      setCalcing(false);
    }
  }, [product, cfg]);
  useEffect(() => { const t = setTimeout(compute, 250); return () => clearTimeout(t); }, [compute]);

  const size = product.sizes.find((s) => s.key === cfg.sizeKey);
  const print = product.color_options.find((o) => o.key === cfg.printKey);
  const turnaround = product.turnaround_options.find((t) => t.key === cfg.turnaroundKey);

  const addToCart = () => {
    if (!price) return;
    addItem({
      product_id: product.product_id,
      product_name: product.name,
      image_url: product.image_url,
      quantity: cfg.qty,
      material_key: product.materials[0].key,
      material_name: product.materials[0].name,
      size: size?.name, size_key: cfg.sizeKey, size_name: size?.name,
      shape: product.shapes?.[0],
      color: print?.name, color_key: cfg.printKey,
      unit_price: price.unit_price,
      line_total: price.line_total,
      artwork_filename: uploadedFile?.source_filename || uploadedFile?.original_filename || null,
      artwork_file_id: uploadedFile?.file_id || null,
      turnaround_key: cfg.turnaroundKey,
      turnaround: turnaround ? {
        key: turnaround.key, label: turnaround.label, business_days: turnaround.business_days,
        price_mode: turnaround.price_mode, price_value: Number(turnaround.price_value) || 0,
        surcharge: Number(price.turnaround_surcharge) || 0,
      } : null,
    });
    toast.success(`${cfg.qty.toLocaleString()} × ${size?.name} added to cart`);
    navigate("/cart");
  };

  const path = `/shop/product/${product.slug}`;
  const image = productImage(product.image_url);
  const jsonLd = useMemo(() => [
    { id: "product", data: productSchema({ product, url: path, image, price }) },
    { id: "breadcrumb", data: breadcrumbSchema([
      { name: "Home", url: "/" }, { name: "Shop", url: "/shop" },
      { name: "Marketing Materials", url: "/shop/marketing-collateral" }, { name: "Custom Boxes & Packaging", url: "/shop/marketing-collateral/custom-boxes-packaging" }, { name: product.name, url: path },
    ]) },
    (() => { const d = faqJsonLd(product.faqs || []); return d ? { id: "faq", data: d } : null; })(),
  ].filter(Boolean), [product, path, image, price]);
  useSeoHead({ title: product.meta_title, description: product.meta_description, canonical: path, image, ogType: "product", jsonLd });

  return (
    <div className="bg-white" data-testid="box-product-page">
      <BoxOptionsStrip currentSlug={product.slug} serviceCategory={product.service_category} subCategory={product.sub_category} />
      <div className="container-page py-8 sm:py-12">
        <nav className="text-xs font-mono text-slate-500 mb-6" aria-label="Breadcrumb" data-testid="box-breadcrumb">
          <Link to="/shop" className="hover:text-kolt-orange">Shop</Link> <span className="mx-2">/</span>
          <Link to="/shop/marketing-collateral" className="hover:text-kolt-orange">Marketing Materials</Link> <span className="mx-2">/</span> <Link to="/shop/marketing-collateral/custom-boxes-packaging" className="hover:text-kolt-orange">Custom Boxes &amp; Packaging</Link> <span className="mx-2">/</span>
          <span className="text-kolt-slate">{product.name}</span>
        </nav>
        <div className="grid lg:grid-cols-[1fr_460px] xl:grid-cols-[1fr_520px] gap-10">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-kolt-slate" data-testid="box-title">{product.name}</h1>
            <div className="flex items-center gap-2 mt-2 text-sm text-slate-600">
              <span className="flex text-kolt-orange">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-4 h-4 fill-current" />)}</span>
              <span>Proof-approved before every print run</span>
            </div>
            <p className="mt-4 text-base text-slate-700 leading-relaxed max-w-2xl" data-testid="box-short-description">{product.short_description}</p>
            <div className="mt-6"><BoxGallery images={product.image_gallery} captions={product.gallery_captions} name={product.name} /></div>
            <ul className="mt-6 grid sm:grid-cols-2 gap-2" data-testid="box-features">
              {(product.features || []).map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-slate-700"><span className="mt-1.5 w-1.5 h-1.5 bg-kolt-orange shrink-0" />{f}</li>
              ))}
            </ul>
          </div>
          <div className="space-y-6 lg:sticky lg:top-24 self-start">
            <BoxConfigurator
              product={product} user={user}
              sizeKey={cfg.sizeKey} setSizeKey={set("sizeKey")}
              printKey={cfg.printKey} setPrintKey={set("printKey")}
              qty={cfg.qty} setQty={set("qty")}
              turnaroundKey={cfg.turnaroundKey}
              price={price} calcing={calcing}
              uploadedFile={uploadedFile} setUploadedFile={setUploadedFile}
              onAddToCart={addToCart} onReset={() => setCfg(DEFAULTS(product))}
            />
            <div className="bg-white border border-slate-200 p-5" data-testid="box-sample-block">
              <div className="overline mb-3">Need an example?</div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setCfg((c) => ({ ...c, qty: 1 }))} className="btn-outline py-3 px-3 text-[10px]" data-testid="box-order-proof-btn">Order a printed proof</button>
                <Link to="/contact" className="btn-outline py-3 px-3 text-[10px]" data-testid="box-request-sample-btn">Request a blank sample</Link>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-8">
          <PriceGrid product={product} sizeKey={cfg.sizeKey} printKey={cfg.printKey} qty={cfg.qty} onPick={set("qty")} />
          <BoxSizeSpecs product={product} sizeKey={cfg.sizeKey} onSelectSize={set("sizeKey")} />
        </div>
        <div className="mt-14"><BoxTeasers teasers={product.teasers} /></div>
        <div className="mt-14"><BoxDetails product={product} /></div>
      </div>
    </div>
  );
}
